import { useState, useEffect, useRef } from 'react';

const GeopoliticalSentimentIndex = () => {
  const [indexValue, setIndexValue] = useState(null);
  const [assets, setAssets] = useState({
    war: { name: '$WAR Token', price: null, change: 0, history: [], weight: 50, subtitle: 'Geopolitical Risk', color: '#ef4444' },
    oil: { name: 'WTI Crude', price: null, change: 0, history: [], weight: 30, subtitle: 'Energy Markets', color: '#f97316' },
    gold: { name: 'Gold XAU/USD', price: null, change: 0, history: [], weight: 20, subtitle: 'Safe Haven', color: '#eab308' }
  });
  const [indexHistory, setIndexHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const flashTimers = useRef({});

  // Helper: Clamp value between min and max
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  // Helper: Get risk band based on index value
  const getRiskBand = (value) => {
    if (value >= 75) return { label: 'CRITICAL', color: '#ef4444' };
    if (value >= 50) return { label: 'HIGH RISK', color: '#f97316' };
    if (value >= 25) return { label: 'ELEVATED', color: '#eab308' };
    return { label: 'CALM', color: '#22c55e' };
  };

  // Helper: Get signal interpretation
  const getSignalInterpretation = (value) => {
    if (value >= 75) return 'CRITICAL — Reduce risk exposure, increase gold allocation';
    if (value >= 50) return 'HIGH RISK — Defensive positioning recommended';
    if (value >= 25) return 'ELEVATED — Watch for breakout triggers';
    return 'CALM — Standard allocation strategy';
  };

  // Fetch data from APIs
  const fetchData = async () => {
    try {
      setStale(false);
      
      // Fetch $WAR token from CoinGecko
      const warPromise = fetch('https://api.coingecko.com/api/v3/simple/price?ids=war&vs_currencies=usd&include_24hr_change=true')
        .then(res => res.json())
        .catch(() => null);

      // For demo purposes, using mock data for oil and gold
      // In production, replace with Alpha Vantage API calls
      const oilPromise = Promise.resolve({ price: 78.45, change: 2.3 });
      const goldPromise = Promise.resolve({ price: 2045.30, change: 0.8 });

      const [warData, oilData, goldData] = await Promise.all([warPromise, oilPromise, goldPromise]);

      // Process $WAR data
      let warPrice = 0.085;
      let warChange = 0;
      if (warData && warData.war) {
        warPrice = warData.war.usd || 0.085;
        warChange = warData.war.usd_24h_change || 0;
      }

      // Update assets state with new prices
      setAssets(prev => {
        const newAssets = { ...prev };
        
        // Update WAR
        newAssets.war = {
          ...prev.war,
          price: warPrice,
          change: warChange,
          history: [...prev.war.history.slice(-39), warPrice]
        };

        // Update Oil
        newAssets.oil = {
          ...prev.oil,
          price: oilData.price,
          change: oilData.change,
          history: [...prev.oil.history.slice(-39), oilData.price]
        };

        // Update Gold
        newAssets.gold = {
          ...prev.gold,
          price: goldData.price,
          change: goldData.change,
          history: [...prev.gold.history.slice(-39), goldData.price]
        };

        return newAssets;
      });

      // Calculate composite index
      const normalizedWarChange = clamp(warChange + 50, 0, 100);
      const normalizedOilChange = clamp(oilData.change * 10 + 50, 0, 100);
      const normalizedGoldChange = clamp(goldData.change * 10 + 50, 0, 100);
      
      const compositeIndex = Math.round(
        (normalizedWarChange * 0.5) + 
        (normalizedOilChange * 0.3) + 
        (normalizedGoldChange * 0.2)
      );

      setIndexValue(compositeIndex);
      setIndexHistory(prev => [...prev.slice(-39), compositeIndex]);
      setLastUpdate(new Date());
      setLoading(false);

      // Trigger flash animations
      triggerFlash('war', warChange);
      triggerFlash('oil', oilData.change);
      triggerFlash('gold', goldData.change);

    } catch (error) {
      console.error('Error fetching geopolitical data:', error);
      setStale(true);
      setLoading(false);
    }
  };

  // Trigger flash animation on price update
  const triggerFlash = (assetKey, change) => {
    const flashColor = change >= 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)';
    const element = document.getElementById(`asset-card-${assetKey}`);
    if (element) {
      element.style.backgroundColor = flashColor;
      clearTimeout(flashTimers.current[assetKey]);
      flashTimers.current[assetKey] = setTimeout(() => {
        element.style.backgroundColor = '#0d0f12';
      }, 300);
    }
  };

  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every 60 seconds
    return () => {
      clearInterval(interval);
      Object.values(flashTimers.current).forEach(clearTimeout);
    };
  }, []);

  // Render Sparkline SVG
  const renderSparkline = (history, color) => {
    if (history.length < 2) return null;

    const width = 120;
    const height = 40;
    const padding = 4;
    const max = Math.max(...history);
    const min = Math.min(...history);
    const range = max - min || 1;

    const points = history.map((value, i) => {
      const x = (i / (history.length - 1)) * (width - padding * 2) + padding;
      const y = height - padding - ((value - min) / range) * (height - padding * 2);
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg width={width} height={height} style={{ display: 'block' }}>
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  // Render Gauge SVG
  const renderGauge = () => {
    if (indexValue === null) return null;

    const size = 240;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const cx = size / 2;
    const cy = size / 2;
    
    // Arc parameters (270-degree sweep from -135deg to +135deg)
    const startAngle = -135;
    const endAngle = 135;
    const totalAngle = endAngle - startAngle;

    // Create arc path for each risk band
    const createArc = (start, end, color) => {
      const startRad = (start * Math.PI) / 180;
      const endRad = (end * Math.PI) / 180;
      const x1 = cx + radius * Math.cos(startRad);
      const y1 = cy + radius * Math.sin(startRad);
      const x2 = cx + radius * Math.cos(endRad);
      const y2 = cy + radius * Math.sin(endRad);
      const largeArc = end - start > 180 ? 1 : 0;

      return (
        <path
          d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
      );
    };

    // Calculate band angles
    const bands = [
      { start: -135, end: -67.5, color: '#22c55e', label: 'CALM' },
      { start: -67.5, end: 0, color: '#eab308', label: 'ELEVATED' },
      { start: 0, end: 67.5, color: '#f97316', label: 'HIGH' },
      { start: 67.5, end: 135, color: '#ef4444', label: 'CRITICAL' }
    ];

    // Calculate needle angle
    const needleAngle = startAngle + (indexValue / 100) * totalAngle;
    const needleRad = (needleAngle * Math.PI) / 180;
    const needleLength = radius - 10;
    const needleX = cx + needleLength * Math.cos(needleRad);
    const needleY = cy + needleLength * Math.sin(needleRad);

    const currentBand = getRiskBand(indexValue);

    return (
      <div style={{ textAlign: 'center' }}>
        <svg width={size} height={size * 0.7} style={{ display: 'block', margin: '0 auto' }}>
          {/* Background arc bands */}
          {bands.map((band, i) => (
            <g key={i}>
              {createArc(band.start, band.end, band.color + '40')}
            </g>
          ))}
          
          {/* Active arc up to current value */}
          {createArc(startAngle, needleAngle, currentBand.color)}
          
          {/* Needle */}
          <line
            x1={cx}
            y1={cy}
            x2={needleX}
            y2={needleY}
            stroke={currentBand.color}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx={cx} cy={cy} r="8" fill={currentBand.color} />
        </svg>
        
        <div style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '48px', fontWeight: 'bold', color: currentBand.color, fontFamily: 'IBM Plex Mono, monospace' }}>
            {indexValue}
          </div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: currentBand.color, marginTop: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
            {currentBand.label}
          </div>
        </div>
      </div>
    );
  };

  // Skeleton loader
  if (loading && indexValue === null) {
    return (
      <div style={{ 
        background: '#0d0f12', 
        borderRadius: '12px', 
        padding: '32px', 
        border: '1px solid #1e2530',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <div style={{ width: '24px', height: '24px', background: '#1e2530', borderRadius: '4px' }}></div>
          <div style={{ width: '200px', height: '24px', background: '#1e2530', borderRadius: '4px' }}></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ background: '#07080a', borderRadius: '8px', padding: '24px', height: '200px' }}>
              <div style={{ width: '100%', height: '100%', background: '#1e2530', borderRadius: '4px' }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const currentBand = indexValue !== null ? getRiskBand(indexValue) : { label: '', color: '#666' };

  return (
    <div style={{ 
      background: '#0d0f12', 
      borderRadius: '12px', 
      padding: '32px', 
      border: '1px solid #1e2530',
      marginBottom: '24px',
      boxShadow: indexValue !== null ? `0 0 40px ${currentBand.color}2E` : 'none',
      transition: 'box-shadow 0.5s ease'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '24px' }}>🌍</span>
          <div>
            <h3 style={{ 
              margin: 0, 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#f3f4f6',
              fontFamily: 'IBM Plex Mono, monospace'
            }}>
              Geopolitical Sentiment Index
            </h3>
            <p style={{ 
              margin: '4px 0 0 0', 
              fontSize: '13px', 
              color: '#9ca3af',
              fontFamily: 'IBM Plex Mono, monospace'
            }}>
              Real-time risk composite from $WAR, Oil, and Gold
            </p>
          </div>
        </div>
        
        {stale && (
          <div style={{ 
            background: '#f9731620', 
            border: '1px solid #f97316', 
            borderRadius: '6px', 
            padding: '6px 12px',
            fontSize: '12px',
            color: '#f97316',
            fontWeight: '600',
            fontFamily: 'IBM Plex Mono, monospace'
          }}>
            STALE
          </div>
        )}
        
        {lastUpdate && !stale && (
          <div style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            fontFamily: 'IBM Plex Mono, monospace'
          }}>
            Updated {lastUpdate.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Main Grid: Gauge + Index Trend */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '24px',
        marginBottom: '32px'
      }}>
        {/* Gauge Card */}
        <div style={{ 
          background: '#07080a', 
          borderRadius: '12px', 
          padding: '24px',
          border: '1px solid #1e2530',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {renderGauge()}
        </div>

        {/* Index Trend Card */}
        <div style={{ 
          background: '#07080a', 
          borderRadius: '12px', 
          padding: '24px',
          border: '1px solid #1e2530'
        }}>
          <h4 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontFamily: 'IBM Plex Mono, monospace'
          }}>
            Index Trend (40-Point)
          </h4>
          {indexHistory.length > 1 ? (
            <div>
              {renderSparkline(indexHistory, currentBand.color)}
              <div style={{ 
                marginTop: '16px', 
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: '12px',
                color: '#6b7280',
                fontFamily: 'IBM Plex Mono, monospace'
              }}>
                <span>Min: {Math.min(...indexHistory)}</span>
                <span>Max: {Math.max(...indexHistory)}</span>
                <span>Avg: {Math.round(indexHistory.reduce((a, b) => a + b, 0) / indexHistory.length)}</span>
              </div>
            </div>
          ) : (
            <div style={{ 
              height: '120px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: '#6b7280',
              fontSize: '14px',
              fontFamily: 'IBM Plex Mono, monospace'
            }}>
              Collecting data...
            </div>
          )}
        </div>
      </div>

      {/* Asset Cards Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        {Object.entries(assets).map(([key, asset]) => (
          <div
            key={key}
            id={`asset-card-${key}`}
            style={{ 
              background: '#0d0f12', 
              borderRadius: '8px', 
              padding: '20px',
              border: '1px solid #1e2530',
              borderTop: `3px solid ${asset.color}`,
              transition: 'background-color 0.3s ease'
            }}
          >
            {/* Asset Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
              <div>
                <h5 style={{ 
                  margin: 0, 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#f3f4f6',
                  fontFamily: 'IBM Plex Mono, monospace'
                }}>
                  {asset.name}
                </h5>
                <p style={{ 
                  margin: '2px 0 0 0', 
                  fontSize: '11px', 
                  color: '#6b7280',
                  fontFamily: 'IBM Plex Mono, monospace'
                }}>
                  {asset.subtitle}
                </p>
              </div>
              <div style={{ 
                background: `${asset.color}20`, 
                border: `1px solid ${asset.color}`,
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '11px',
                fontWeight: '600',
                color: asset.color,
                fontFamily: 'IBM Plex Mono, monospace'
              }}>
                {asset.weight}%
              </div>
            </div>

            {/* Price */}
            <div style={{ marginBottom: '8px' }}>
              <div style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                color: '#f3f4f6',
                fontFamily: 'IBM Plex Mono, monospace'
              }}>
                ${asset.price !== null ? asset.price.toFixed(key === 'war' ? 4 : 2) : '--'}
              </div>
              <div style={{ 
                fontSize: '14px', 
                fontWeight: '600',
                color: asset.change >= 0 ? '#22c55e' : '#ef4444',
                fontFamily: 'IBM Plex Mono, monospace'
              }}>
                {asset.change >= 0 ? '+' : ''}{asset.change.toFixed(2)}%
              </div>
            </div>

            {/* Sparkline */}
            <div style={{ marginTop: '12px' }}>
              {asset.history.length > 1 ? (
                renderSparkline(asset.history, asset.color)
              ) : (
                <div style={{ 
                  height: '40px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: '#4b5563',
                  fontSize: '11px',
                  fontFamily: 'IBM Plex Mono, monospace'
                }}>
                  Building history...
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Signal Interpretation Panel */}
      <div style={{ 
        background: `${currentBand.color}10`, 
        border: `1px solid ${currentBand.color}40`,
        borderRadius: '8px',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <div style={{ 
          background: currentBand.color,
          borderRadius: '8px',
          padding: '12px 16px',
          fontSize: '12px',
          fontWeight: '700',
          color: '#000',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          fontFamily: 'IBM Plex Mono, monospace',
          whiteSpace: 'nowrap'
        }}>
          {currentBand.label}
        </div>
        <div style={{ 
          fontSize: '14px', 
          color: '#e5e7eb',
          fontFamily: 'IBM Plex Mono, monospace',
          lineHeight: '1.6'
        }}>
          {indexValue !== null ? getSignalInterpretation(indexValue) : 'Calculating...'}
        </div>
      </div>
    </div>
  );
};

export default GeopoliticalSentimentIndex;
